import IconChevronRight from "central-icons/IconChevronRight";
import { Pill } from "@/components/podkit/status/Pill";
import {
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionContent,
    AccordionTrigger,
} from "@radix-ui/react-accordion";
import {
    type EnvironmentGitStatus,
    type FileChange,
    FileChange_ChangeType,
    EnvironmentPhase,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState, type FC } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { cva } from "class-variance-authority";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useFileDiffContent, useEnvironmentGitDiff, type PlainEnvironment } from "@/queries/environment-queries";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Text } from "@/components/podkit/typography/Text";
import { ContentDiffView, GitDiffView } from "@/routes/environments/code-changes/GitDiffView";
import { extractNewContentFromHunks } from "@/routes/environments/code-changes/diff-utils";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import { MarkdownPreview } from "@/routes/environments/code-changes/MarkdownPreview";
import { effectiveState, showStart } from "@/routes/environments/phase";
import type { ReviewComment, CommentsByFile } from "@/components/ai/review/types";

import { sortFilesWithPinned } from "@/routes/environments/code-changes/file-change-utils";

export type FileChangeListRef = {
    addScrollListener: (listener: () => void) => void;
    removeScrollListener: (listener: () => void) => void;
    scrollToFile: (path: string) => void;
    setFilePreviewMode: (path: string) => void;
};

export type FileChangeListProps = {
    environment: PlainEnvironment;
    gitStatus: PlainMessage<EnvironmentGitStatus>;
    className?: string;
    openedFiles: string[];
    onOpenedFilesChange: (openedFiles: string[]) => void;
    /** Review comments grouped by file path */
    commentsByFile?: CommentsByFile;
    /** Callback to add a comment */
    onAddComment?: (
        filePath: string,
        startLine: number,
        endLine: number,
        content: string,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    /** Callback to update an existing comment */
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    /** Callback to delete a comment */
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    /** Callback to set thumbs up on a comment */
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    /** Callback to set thumbs down on a comment */
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
    /** When set, files whose path ends with this suffix are pinned to the top of the list */
    pinnedFileSuffix?: string;
};

export const FileChangeList = forwardRef<FileChangeListRef, FileChangeListProps>(
    (
        {
            environment,
            gitStatus,
            className,
            openedFiles,
            onOpenedFilesChange,
            commentsByFile = {},
            onAddComment,
            onUpdateComment,
            onDeleteComment,
            onSetCommentThumbsUp,
            onSetCommentThumbsDown,
            pinnedFileSuffix,
        },
        ref,
    ) => {
        const accordionRef = useRef<HTMLDivElement>(null);
        const [previewModeFiles, setPreviewModeFiles] = useState<Set<string>>(new Set());

        useImperativeHandle(
            ref,
            (): FileChangeListRef => ({
                addScrollListener: (listener) => {
                    accordionRef.current?.addEventListener("scroll", listener);
                },
                removeScrollListener: (listener) => {
                    accordionRef.current?.removeEventListener("scroll", listener);
                },
                scrollToFile: (path) => {
                    if (accordionRef.current) {
                        const element = document.getElementById(path);
                        element?.scrollIntoView();
                    }
                },
                setFilePreviewMode: (path) => {
                    setPreviewModeFiles((prev) => new Set(prev).add(path));
                },
            }),
            [],
        );

        const sortedFiles = useMemo(
            () => sortFilesWithPinned(gitStatus.changedFiles ?? [], pinnedFileSuffix),
            [gitStatus.changedFiles, pinnedFileSuffix],
        );

        return (
            <Accordion
                ref={accordionRef}
                type="multiple"
                className={cn("w-full space-y-2", className)}
                value={openedFiles}
                onValueChange={onOpenedFilesChange}
            >
                {sortedFiles.map((file) => (
                    <FileChangeItem
                        key={file.path}
                        environment={environment}
                        file={file}
                        open={openedFiles.includes(file.path)}
                        comments={commentsByFile[file.path] ?? []}
                        onAddComment={onAddComment}
                        onUpdateComment={onUpdateComment}
                        onDeleteComment={onDeleteComment}
                        onSetCommentThumbsUp={onSetCommentThumbsUp}
                        onSetCommentThumbsDown={onSetCommentThumbsDown}
                        defaultPreviewMode={previewModeFiles.has(file.path)}
                    />
                ))}
            </Accordion>
        );
    },
);
FileChangeList.displayName = "FileChangeList";

export const FileChangeListSkeleton = ({ className }: { className?: string }) => {
    return (
        <div className={cn("flex w-full flex-col space-y-2", className)}>
            {Array.from({ length: 10 }).map((_, index) => (
                <SkeletonBlock className="h-12 w-full bg-surface-muted" key={index} ready={false} />
            ))}
        </div>
    );
};

const gitStatusDotVariants = cva(
    "flex size-4 items-center justify-center rounded-full bg-surface-accent text-xs font-medium select-none",
    {
        variants: {
            status: {
                [FileChange_ChangeType.UNSPECIFIED]: null,
                [FileChange_ChangeType.MODIFIED]: "bg-surface-accent text-content-muted",
                [FileChange_ChangeType.DELETED]: "bg-surface-warning-subtle text-content-destructive",
                [FileChange_ChangeType.ADDED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.UNTRACKED]: "bg-surface-warning-subtle text-content-warning",
                [FileChange_ChangeType.RENAMED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.COPIED]: "bg-surface-success-subtle text-content-success",
                [FileChange_ChangeType.UPDATED_BUT_UNMERGED]: "bg-surface-warning-subtle text-content-warning",
            },
        },
    },
);

const CHANGE_TYPE_LABEL = {
    [FileChange_ChangeType.UNSPECIFIED]: null,
    [FileChange_ChangeType.MODIFIED]: { symbol: "M", full: "modified" },
    [FileChange_ChangeType.DELETED]: { symbol: "D", full: "deleted" },
    [FileChange_ChangeType.ADDED]: { symbol: "A", full: "added" },
    [FileChange_ChangeType.UNTRACKED]: { symbol: "U", full: "untracked" },
    [FileChange_ChangeType.RENAMED]: { symbol: "R", full: "renamed" },
    [FileChange_ChangeType.COPIED]: { symbol: "C", full: "copied" },
    [FileChange_ChangeType.UPDATED_BUT_UNMERGED]: { symbol: "!", full: "unmerged" },
} as const;

export const GitStatusDot: FC<{ status: FileChange_ChangeType }> = (props) => {
    const label = CHANGE_TYPE_LABEL[props.status];
    if (!label) return null;

    return (
        <span aria-hidden="true" className={cn(gitStatusDotVariants({ status: props.status }))}>
            {label.symbol}
        </span>
    );
};

const isMarkdownFile = (path: string): boolean => {
    const lowerPath = path.toLowerCase();
    return lowerPath.endsWith(".md") || lowerPath.endsWith(".markdown");
};

type ViewMode = "diff" | "preview";

type FileChangeItemProps = {
    environment: PlainEnvironment;
    file: PlainMessage<FileChange>;
    open: boolean;
    comments?: ReviewComment[];
    onAddComment?: (
        filePath: string,
        startLine: number,
        endLine: number,
        content: string,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
    defaultPreviewMode?: boolean;
};

const FileChangeItem = memo(
    ({
        environment,
        file,
        open,
        comments = [],
        onAddComment,
        onUpdateComment,
        onDeleteComment,
        onSetCommentThumbsUp,
        onSetCommentThumbsDown,
        defaultPreviewMode = false,
    }: FileChangeItemProps) => {
        const fileName = useMemo(() => {
            return file.path.split("/").pop();
        }, [file.path]);

        const isMarkdown = isMarkdownFile(file.path);
        const [viewMode, setViewMode] = useState<ViewMode>(defaultPreviewMode && isMarkdown ? "preview" : "diff");

        // Update view mode when defaultPreviewMode changes (e.g., when file is opened with preview)
        useEffect(() => {
            if (defaultPreviewMode && isMarkdown) {
                setViewMode("preview");
            }
        }, [defaultPreviewMode, isMarkdown]);

        const handleViewModeChange = (e: React.MouseEvent, mode: ViewMode) => {
            e.stopPropagation();
            setViewMode(mode);
        };

        return (
            <AccordionItem
                className={cn("overflow-clip rounded-lg border border-border-base bg-surface-primary")}
                id={file.path}
                value={file.path}
            >
                <AccordionHeader className="hover:bg-surface-hover">
                    <AccordionTrigger className="group grid w-full grid-cols-[min-content_minmax(0,1fr)] grid-rows-[min-content_min-content] border-b border-b-transparent data-[state=open]:border-b-border-base data-[state=open]:bg-surface-muted">
                        <IconChevronRight className="col-span-1 row-span-2 m-2 size-4 self-center text-content-secondary transition-transform group-data-[state=open]:rotate-90" />
                        <div className="col-span-1 row-span-1 flex min-w-0 flex-row items-center justify-between self-end pr-2 pt-2">
                            <Text className="truncate text-base font-medium">{fileName}</Text>
                            <div className="flex items-center gap-1">
                                {isMarkdown && open && (
                                    <div className="flex rounded-md border border-border-base bg-surface-secondary text-xs">
                                        <button
                                            type="button"
                                            data-tracking-id="file-change-view-mode-diff"
                                            onClick={(e) => handleViewModeChange(e, "diff")}
                                            className={cn(
                                                "rounded-l-md px-2 py-0.5 transition-colors",
                                                viewMode === "diff"
                                                    ? "bg-surface-primary text-content-primary"
                                                    : "text-content-secondary hover:text-content-primary",
                                            )}
                                        >
                                            Diff
                                        </button>
                                        <button
                                            type="button"
                                            data-tracking-id="file-change-view-mode-preview"
                                            onClick={(e) => handleViewModeChange(e, "preview")}
                                            className={cn(
                                                "rounded-r-md px-2 py-0.5 transition-colors",
                                                viewMode === "preview"
                                                    ? "bg-surface-primary text-content-primary"
                                                    : "text-content-secondary hover:text-content-primary",
                                            )}
                                        >
                                            Preview
                                        </button>
                                    </div>
                                )}
                                {comments.length > 0 && (
                                    <Pill
                                        size="sm"
                                        className="h-4"
                                        variant="brand"
                                        LeadingIcon={() => <IconBubbleText6 size={12} />}
                                    >
                                        {comments.length}
                                        <span className="sr-only">comments</span>
                                    </Pill>
                                )}
                                <GitStatusDot status={file.changeType} />
                            </div>
                        </div>
                        <Text className="col-span-1 row-span-1 min-w-0 self-start text-wrap break-words pb-2 pr-4 text-start font-mono text-sm text-content-muted">
                            {file.path}
                        </Text>
                    </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent>
                    <FileChangeItemContent
                        environment={environment}
                        file={file}
                        open={open}
                        viewMode={viewMode}
                        comments={comments}
                        onAddComment={onAddComment}
                        onUpdateComment={onUpdateComment}
                        onDeleteComment={onDeleteComment}
                        onSetCommentThumbsUp={onSetCommentThumbsUp}
                        onSetCommentThumbsDown={onSetCommentThumbsDown}
                    />
                </AccordionContent>
            </AccordionItem>
        );
    },
);
FileChangeItem.displayName = "FileChangeItem";

type FileChangeItemContentProps = {
    environment: PlainEnvironment;
    file: PlainMessage<FileChange>;
    open: boolean;
    viewMode: ViewMode;
    comments?: ReviewComment[];
    onAddComment?: (
        filePath: string,
        startLine: number,
        endLine: number,
        content: string,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
};

const FileChangeItemContent = memo(
    ({
        environment,
        file,
        open,
        viewMode,
        comments,
        onAddComment,
        onUpdateComment,
        onDeleteComment,
        onSetCommentThumbsUp,
        onSetCommentThumbsDown,
    }: FileChangeItemContentProps) => {
        const envState = effectiveState(environment);
        const starting =
            envState.state === EnvironmentPhase.CREATING ||
            envState.state === EnvironmentPhase.STARTING ||
            envState.state === EnvironmentPhase.UPDATING;
        const stopped = showStart(environment);
        const canFetchDiff = open && !starting && !stopped;
        const {
            data: diffContent,
            isPending: isContentPending,
            isError: isContentError,
        } = useFileDiffContent(environment.id, {
            path: file.path,
            enabled: canFetchDiff,
            refetchInterval: canFetchDiff ? 5000 : false,
        });
        const { data: gitDiff, isPending: isHunksPending } = useEnvironmentGitDiff(environment.id, {
            path: file.path,
            enabled: canFetchDiff,
            refetchInterval: canFetchDiff ? 5000 : false,
        });

        // Don't wait for content if the RPC errored (e.g. UNIMPLEMENTED on older runners)
        const isPending = (isContentPending && !isContentError) || isHunksPending;

        const isMarkdown = isMarkdownFile(file.path);

        const originalContent = diffContent?.originalContent ?? "";
        const newContent = diffContent?.newContent ?? "";

        if (!open) return null;

        if (!canFetchDiff) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <IconSpinner className="animate-spin" size="sm" />
                    <Text className="text-sm text-content-muted">Starting environment…</Text>
                </div>
            );
        }

        if (isPending) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <IconSpinner className="animate-spin" size="sm" />
                    <Text className="text-sm text-content-muted">Loading file diff…</Text>
                </div>
            );
        }

        if (!gitDiff) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <Text className="text-sm text-content-muted">Diff unavailable</Text>
                </div>
            );
        }

        if (viewMode === "diff") {
            // Fall back to hunk-based GitDiffView when file content is unavailable
            // (e.g. GetFileDiffContent not implemented on older runners)
            if (!diffContent) {
                return (
                    <GitDiffView
                        hunks={gitDiff.hunks}
                        filePath={file.path}
                        comments={comments}
                        onAddComment={
                            onAddComment
                                ? (content, startLine, endLine, side, contentHash) =>
                                      onAddComment(file.path, startLine, endLine, content, side, contentHash)
                                : undefined
                        }
                        onUpdateComment={onUpdateComment}
                        onDeleteComment={onDeleteComment}
                        onSetCommentThumbsUp={onSetCommentThumbsUp}
                        onSetCommentThumbsDown={onSetCommentThumbsDown}
                    />
                );
            }

            return (
                <ContentDiffView
                    originalContent={originalContent}
                    newContent={newContent}
                    filePath={file.path}
                    hunks={gitDiff.hunks}
                    comments={comments}
                    onAddComment={
                        onAddComment
                            ? (content, startLine, endLine, side, contentHash) =>
                                  onAddComment(file.path, startLine, endLine, content, side, contentHash)
                            : undefined
                    }
                    onUpdateComment={onUpdateComment}
                    onDeleteComment={onDeleteComment}
                    onSetCommentThumbsUp={onSetCommentThumbsUp}
                    onSetCommentThumbsDown={onSetCommentThumbsDown}
                />
            );
        }

        // Preview mode - use new content for markdown preview,
        // falling back to content reconstructed from hunks when GetFileDiffContent is unavailable
        const previewContent = newContent || (gitDiff ? extractNewContentFromHunks(gitDiff.hunks) : "");
        if (!isMarkdown || !previewContent) {
            return (
                <div className="flex items-center gap-2 p-2 text-content-muted">
                    <Text className="text-sm text-content-muted">Preview unavailable</Text>
                </div>
            );
        }

        return (
            <div className="p-4">
                <MarkdownPreview content={previewContent} className="prose prose-sm dark:prose-invert max-w-none" />
            </div>
        );
    },
);
FileChangeItemContent.displayName = "FileChangeItemContent";
